import { useEffect, useId, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SITE } from '~/content/site.js';
import { cn } from '~/lib/cn.js';
import { Logo } from './Logo.js';
import { ThemeToggle } from './ThemeToggle.js';

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const panelId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);

  const scrollToRoles = (event: MouseEvent<HTMLAnchorElement>) => {
    setOpen(false);
    if (location.pathname !== '/') return;

    // React Router does not re-run a navigation when the current hash already
    // matches. Scroll explicitly so this remains useful after someone has moved
    // farther down the home page.
    event.preventDefault();
    window.history.pushState(null, '', '/#open-roles');
    document.getElementById('open-roles')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Any navigation closes the panel; otherwise it would hang open over the new
  // page. Adjusting during render rather than in an effect avoids a second pass.
  const [lastLocationKey, setLastLocationKey] = useState(location.key);
  if (location.key !== lastLocationKey) {
    setLastLocationKey(location.key);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-page/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-(--container-content) items-center justify-between gap-4 px-5 py-3.5 sm:px-8">
        <Logo />

        <nav aria-label="Primary" className="hidden items-center gap-7 md:flex">
          <a
            href={SITE.mainSiteUrl}
            className="text-sm text-body hover:text-primary"
            rel="noopener noreferrer"
          >
            Studio website <span aria-hidden="true">↗</span>
          </a>
          <Link
            to="/#open-roles"
            onClick={scrollToRoles}
            className="inline-flex min-h-10 items-center rounded-full bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-secondary dark:text-[#071427]"
          >
            Open positions
          </Link>
          <ThemeToggle />
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <button
            ref={toggleRef}
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex min-h-11 items-center rounded-full border border-line-strong px-4 text-sm text-heading"
          >
            {open ? 'Close' : 'Menu'}
          </button>
        </div>
      </div>

      <div id={panelId} hidden={!open} className={cn('border-t border-line bg-page md:hidden')}>
        <nav
          aria-label="Primary"
          className="mx-auto flex max-w-(--container-content) flex-col px-5 py-2 sm:px-8"
        >
          <Link
            to="/#open-roles"
            onClick={scrollToRoles}
            className="my-2 inline-flex min-h-11 items-center justify-center rounded-full bg-primary px-5 text-sm font-medium text-white dark:text-[#071427]"
          >
            View open positions
          </Link>
          <a
            href={SITE.mainSiteUrl}
            className="py-3 text-base text-body hover:text-primary"
            rel="noopener noreferrer"
          >
            Studio website <span aria-hidden="true">↗</span>
          </a>
        </nav>
      </div>
    </header>
  );
}
